/** @typedef {import('reducers/activity/activityTypesTypeDefinitions').ActivityTypeDTO} ActivityTypeDTO */

import { string } from 'prop-types';
import ActivitiesUtil from 'utils/activity/ActivitiesUtil';

/**
 * Utility for activity types
 */

// Based on requirements from the Parent Level Privacy project
export const parentTypeDisplayOrder = [
    'running',
    'cycling',
    'fitness_equipment',
    'swimming',
    'walking',
    'hiking',
    'multi_sport',
    'diving',
    'winter_sports',
    'water_sports',
    'team_sports',
    'racket_sports',
    'para_sports',
    'other',
    'safety'
];
export default class ActivityTypesUtil {
    constructor(activityTypes) {
        this.activityTypes = activityTypes;
        this.idMap = null;
    }

    findByTypeId = function(typeId) {
        if (!this.idMap) {
            var idMap = {};
            this.activityTypes.forEach((activityType) => {
                idMap[activityType['typeId']] = activityType;
            });
            this.idMap = idMap;
        }

        return this.idMap[typeId];
    };

    findByTypeKey = function(typeKey) {
        return this.activityTypes.find((activityType) => {
            return typeKey === activityType['typeKey'];
        });
    };

    getParentType = function(activityTypeKey) {
        var activityType = this.findByTypeKey(activityTypeKey);
        if (activityType) {
            var parentId = activityType['parentTypeId'];
            if (!parentId) {
                return activityType;
            } else {
                var parentActivityType = this.findByTypeId(parentId);
                if (parentActivityType['typeKey'] === 'all') {
                    return activityType;
                } else {
                    return parentActivityType;
                }
            }
        }
        return null;
    };
}

// Return the position that a parent activity type should be displayed at in a list of activity types
export const getParentTypeDisplayOrderIndex = function(typeKey) {
    for (let i = 0, length = parentTypeDisplayOrder.length; i < length; i++) {
        if (typeKey === parentTypeDisplayOrder[i]) {
            return i;
        }
    }

    return -1; // Not a parent activity type
};

/**
 *
 * @param {ActivityTypeDTO[]} types
 * @param {number} parentTypeId
 * @param {string} [sortBy]
 */
export const getActivitiesOfParentById = (types, parentTypeId, sortBy) => {
    let activityTypes = types.filter((activityType) => {
        return activityType.parentTypeId === parentTypeId && activityType.typeKey !== 'steps';
    });
    if (sortBy) {
        activityTypes.sort((curr, next) => {
            return curr[sortBy] < next[sortBy] ? -1 : 1;
        });
    }
    return activityTypes;
};

// return activity subcategories with order
export const getActivitySubcategoryInOrder = function(listOfActivitySubcategory) {
    let returnObj = [];

    if (listOfActivitySubcategory && listOfActivitySubcategory.length) {
        //remove reference
        returnObj = [...listOfActivitySubcategory];

        returnObj.forEach((category) => {
            let typeKey = category.privacySubcategoryType;
            //API inconsistency - need to map to be the same as parentTypeDisplayOrder - privacy activity type is different from activity types endpoint
            if (typeKey === 'winter_sport') {
                typeKey = 'winter_sports';
            }

            category.displayOrder = getParentTypeDisplayOrderIndex(typeKey);
        });

        returnObj.sort((a, b) => (a.displayOrder < b.displayOrder ? -1 : 0));
    }

    return returnObj;
};

/**
 * This returns a JSON representation of this collection but with the children being
 * output as children of the parent activities.
 */
export const toParentChildFormat = function(activityTypes) {
    const activityTypesUtil = new ActivityTypesUtil(activityTypes);

    // find the root activity type
    var rootActivityType = activityTypesUtil.findByTypeKey('all');
    var rootTypeId = rootActivityType?.typeId;

    var parentActivityTypes = [];

    // First, find parents.
    activityTypes.forEach((activityType) => {
        if (activityType.parentTypeId === rootTypeId) {
            var newActivityType = {
                ...activityType
            };

            // Note that children is not an attribute. This is best practice. Not to mix
            // persisted properties with ones created at runtime.
            newActivityType.children = [];
            parentActivityTypes.push(newActivityType);
        }
    });

    // Now add the children
    activityTypes.forEach((activityType) => {
        if (activityType.typeId !== rootTypeId && activityType.parentTypeId !== rootTypeId) {
            var parentTypeId = activityType.parentTypeId;

            var parentActivity = parentActivityTypes.find(
                (parentActivityType) => parentActivityType.typeId === parentTypeId
            );
            if (parentActivity && parentActivity.children) {
                //parent could be missing if hidden from the api and user does not have roles
                parentActivity.children.push(activityType);
            }
        }
    });

    parentActivityTypes.forEach((parentActivityType) => {
        parentActivityType.children = parentActivityType.children.sort((subtypeA, subtypeB) => {
            if (subtypeA.typeKey < subtypeB.typeKey) {
                return -1;
            }
            if (subtypeA.typeKey > subtypeB.typeKey) {
                return 1;
            }
            return 0;
        });
    });

    return parentActivityTypes;
};

export const filterBannedActivityTypes = (activityTypes) => {
    const bannedTypes = {};
    const bannedActivityTypes = ['multi_sport', 'steps'];

    return activityTypes.filter((activityType) => {
        const contains = bannedActivityTypes.includes(activityType.typeKey);
        if (contains) {
            bannedTypes[activityType.typeKey] = activityType;
        }
        return !contains;
    });
};

export const findBannedTypeByTypeId = (typeId, activityTypes) => {
    const bannedTypes = filterBannedActivityTypes(activityTypes);
    return bannedTypes[typeId];
};

export const filterActivityTypes = (activityTypes) => {
    let filteredActivityTypes = filterBannedActivityTypes(activityTypes);
    if (filteredActivityTypes) {
        filteredActivityTypes = filteredActivityTypes.filter((activityType) => {
            return activityType.typeKey !== 'motorcycling';
        });
    }
    return filteredActivityTypes;
};

export const getTypesForActivity = (activityTypeId, typeKey, formattedActivityTypes) => {
    let obj;
    const arr = [];

    // Search for the activity type parent that this activity is or is a child of
    for (const activityType of formattedActivityTypes) {
        if (activityType.value === activityTypeId) {
            obj = activityType;
        } else if (activityType.children && activityType.children.length > 0) {
            for (const childActivity of activityType.children) {
                if (childActivity.value === activityTypeId) {
                    obj = activityType;
                }
            }
        }
    }
    // Format the result for easier display on dropdown
    if (obj) {
        arr.push(obj);
        if (obj.children) {
            arr.push(...obj.children);
        }
    } else if (typeKey === 'multi_sport') {
        arr.push({
            nameKey: 'activity_type_multi_sport',
            typeKey: 'multi_sport',
            value: activityTypeId
        });
    }

    return arr;
};

export const formatActivityTypesForDropdownList = (
    types,
    excludeSafetyEvents,
    multiSportActivityType,
    bannedActivityTypeKeys
) => {
    if (bannedActivityTypeKeys && bannedActivityTypeKeys.length > 0) {
        bannedActivityTypeKeys.forEach((bannedTypeKey) => {
            types = types.filter((type) => type.typeKey !== bannedTypeKey);
        });
    }

    const activityTypes = toParentChildFormat(types);

    if (multiSportActivityType) {
        activityTypes.push(multiSportActivityType);
    }

    const returnList = [];

    activityTypes.forEach((activityType) => {
        const item = {
            nameKey: getActivityTypeNameKey(activityType.typeKey),
            value: activityType.typeId,
            typeKey: activityType.typeKey,
            children: [],
            restricted: activityType.restricted,
            displayOrder: getParentTypeDisplayOrderIndex(activityType.typeKey)
        };

        if (activityType?.children?.length) {
            activityType.children.forEach((activityType) => {
                item.children.push({
                    nameKey: getActivityTypeNameKey(activityType.typeKey),
                    typeKey: activityType.typeKey,
                    value: activityType.typeId,
                    restricted: activityType.restricted
                });
            });
        }

        if (excludeSafetyEvents) {
            if (!ActivitiesUtil.isSafetyEvent(item.typeKey)) {
                returnList.push(item);
            }
        } else {
            returnList.push(item);
        }
    });

    return returnList;
};

export const getActivityTypeNameKey = (activityTypeKey) => {
    return `activity_type_${activityTypeKey}`;
};

export const filterFormattedActivityTypesByUsage = (activityTypes, performanceBasedIndicators) => {
    // filter by usage indicators
    const indicators = performanceBasedIndicators?.activityIndicators;
    let filteredActivityTypes = activityTypes.filter((activityType) => {
            if (indicators && indicators[activityType.typeKey]) {
                return indicators[activityType.typeKey].lastActivityDate;
            }
        }),
        activityTypesWhichHaveMetrics = Object.keys(ActivitiesUtil.activityListColumnConfigDefaults);

    // filter even more by what activity types have metrics defined. This is to guard against displaying filters for activities without metrics.
    filteredActivityTypes = filteredActivityTypes.filter((activityType) => {
        return activityTypesWhichHaveMetrics.indexOf(activityType.typeKey) > -1;
    });

    return filteredActivityTypes;
};

export const ensureActivityTypesOrder = (activityTypes) => {
    return activityTypes.sort((a, b) => {
        const indexA = parentTypeDisplayOrder.indexOf(a.typeKey);
        const indexB = parentTypeDisplayOrder.indexOf(b.typeKey);
        return indexA - indexB;
    });
};

export const filterActivityTypesByParent = (activityTypes, parentTypeArray) => {
    return activityTypes.filter((activityType) => {
        const found = parentTypeArray.find((parentType) => {
            return activityType.typeKey === parentType;
        });
        return found && found;
    });
};
